import { useEffect, useState } from 'react';

type Props = {
  readonly isTyping: boolean;
  readonly hasPassword: boolean;
  readonly showPassword: boolean;
};

export type CharacterAnimationState = {
  readonly isTyping: boolean;
  readonly hasPassword: boolean;
  readonly showPassword: boolean;
  readonly isPurpleBlinking: boolean;
  readonly isBlackBlinking: boolean;
  readonly isLookingAtEachOther: boolean;
  readonly isPurplePeeking: boolean;
};

const getRandomBlinkInterval = () => Math.random() * 4000 + 3000; // 3-7 seconds
const getRandomPeekInterval = () => Math.random() * 3000 + 2000; // 2-5 seconds

const wait = async (duration: number, signal: AbortSignal) =>
  new Promise<boolean>((resolve) => {
    const timeout = setTimeout(() => {
      resolve(false);
    }, duration);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timeout);
        resolve(true);
      },
      { once: true }
    );
  });

const useRecurringFlag = (isEnabled: boolean, getDelay: () => number, activeDuration: number) => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      setIsActive(false);
      return;
    }

    const controller = new AbortController();
    const runCycle = async (): Promise<void> => {
      const wasDelayAborted = await wait(getDelay(), controller.signal);
      if (wasDelayAborted) {
        return;
      }

      setIsActive(true);
      const wasActivePeriodAborted = await wait(activeDuration, controller.signal);
      if (wasActivePeriodAborted) {
        return;
      }

      setIsActive(false);
      void runCycle();
    };

    void runCycle();
    return () => {
      controller.abort();
    };
  }, [activeDuration, getDelay, isEnabled]);

  return isActive;
};

const useTemporaryFlag = (isEnabled: boolean, duration: number) => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!isEnabled) {
      setIsActive(false);
      return;
    }

    setIsActive(true);
    const timeout = setTimeout(() => {
      setIsActive(false);
    }, duration);

    return () => {
      clearTimeout(timeout);
    };
  }, [duration, isEnabled]);

  return isActive;
};

const useCharacterAnimation = ({
  isTyping,
  hasPassword,
  showPassword,
}: Props): CharacterAnimationState => {
  const isPurpleBlinking = useRecurringFlag(true, getRandomBlinkInterval, 150);
  const isBlackBlinking = useRecurringFlag(true, getRandomBlinkInterval, 150);
  const isLookingAtEachOther = useTemporaryFlag(isTyping, 800);
  const isPurplePeeking = useRecurringFlag(hasPassword && showPassword, getRandomPeekInterval, 800);

  return {
    isTyping,
    hasPassword,
    showPassword,
    isPurpleBlinking,
    isBlackBlinking,
    isLookingAtEachOther,
    isPurplePeeking,
  };
};

export default useCharacterAnimation;
